import {
  queryOptions,
  useQuery,
  type UseQueryResult,
} from "@tanstack/react-query";

import type { ApiClient } from "@/api/client";
import { useApiClient } from "@/api/client-context";

/**
 * そのメッセージ名で起票されるプロセスモデル（無ければ null）。
 *
 * メッセージ名は環境内で一意で、別モデルが使っている名前を保存しようとすると
 * backend が拒否する（spec_file_service: "Message model is already used to start
 * process model ..."）。保存して初めて気づくのでは遅いので、設計画面で先に引く。
 *
 * 照会そのものに失敗した場合（権限不足など）は null に倒す。判断材料が無いだけで、
 * 保存を止める理由にはならない。
 */
export function messageOwnerQueryOptions(
  client: ApiClient,
  messageName: string,
) {
  const fetchOwner = async (): Promise<string | null> => {
    try {
      const found = await client.get<{
        readonly process_model_identifier?: string | null;
      }>(`/messages/${messageName}/process-model`);

      return found.process_model_identifier ?? null;
    } catch {
      return null;
    }
  };

  return queryOptions({
    queryKey: ["messages", "process-model", messageName] as const,
    queryFn: fetchOwner,
    staleTime: 30_000,
  });
}

export function useMessageOwner(
  messageName: string | null,
): UseQueryResult<string | null> {
  const client = useApiClient();

  return useQuery({
    ...messageOwnerQueryOptions(client, messageName ?? ""),
    enabled: messageName !== null && messageName !== "",
  });
}
